import { useMemo } from 'react';
import { toDictionary } from '@bbkAdminRedux/redux_utils';
import type { BBKRequestContextArgs } from '@bbkAdminRedux/rtkq/bbkApi.slice';
import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';

export enum RuleType {
  SPLIT = 'SPLIT',
  TARGETING = 'TARGETING',
}

export type RuleMetadata = {
  id: string;
  display_name: string;
  internal_name: string;
  created_at: string;
  updated_at: string;
  active: boolean;
  application_id: string;
  is_ai: boolean;
  type: RuleType;
};
export type Rule = RuleMetadata & {
  client_formatted_rule: string;
};
type RestRulesMetadataResponse = {
  collection: RuleMetadata[];
};

type GetRulesArgs = {
  appKey: string;
  active?: boolean;
  includeSplitRules?: boolean;
} & BBKRequestContextArgs;
type GetRuleArgs = {
  appKey: string;
  ruleId: string;
};

export type CreateRuleDto = Pick<
  Rule,
  'display_name' | 'client_formatted_rule' | 'active'
>;
export type CreateRuleArgs = {
  appKey: string;
} & CreateRuleDto;

type UpdateRuleDto = Pick<
  Rule,
  'id' | 'display_name' | 'client_formatted_rule' | 'active'
>;
export type UpdateRuleArgs = {
  appKey: string;
  comment: string;
  brand_id?: string;
} & UpdateRuleDto;

export const rulesSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    // GET `/rules` (getRules query) doesn't return `client_formatted_rule`
    // GET `/rules/:ruleId` (getRule query) does
    getRules: build.query<RuleMetadata[], GetRulesArgs>({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/rules`,
        params: {
          active_only: args?.active ?? false,
          context: args.context,
        },
      }),
      transformResponse: (response: RestRulesMetadataResponse) =>
        response.collection,
      providesTags: ['Rules'],
    }),
    getRule: build.query<Rule, GetRuleArgs>({
      query: (args) => ({
        url: `/api/v1/apps/${args.appKey}/rules/${args.ruleId}`,
      }),
      keepUnusedDataFor: 0, // we don't want to cache getRule responses. This request is ad-hoc only
    }),
    createRule: build.mutation<Rule, CreateRuleArgs>({
      query: ({ appKey, ...rule }) => ({
        method: 'POST',
        url: `/api/v1/apps/${appKey}/rules`,
        body: rule,
      }),
      invalidatesTags: ['Rules'],
    }),
    updateRule: build.mutation<Rule, UpdateRuleArgs>({
      query: ({ appKey, comment, brand_id, ...rule }) => ({
        method: 'PUT',
        url: `/api/v1/apps/${appKey}/rules/${rule.id}`,
        body: {
          rule,
          comment,
          brand_id,
        },
      }),
      invalidatesTags: (result, error, arg, meta) => [
        {
          type: 'AdditionalBrands',
          id: arg.brand_id,
        },
        { type: 'Rules' },
      ],
    }),
  }),
});

export const useSelectAllRules = (args: GetRulesArgs) => {
  const { data } = rulesSlice.endpoints.getRules.useQuery(args);
  return useMemo(() => {
    const filteredRules = args.includeSplitRules
      ? data
      : data?.filter((r) => r.type === RuleType.TARGETING);
    return [...(filteredRules ?? [])].sort((a, b) =>
      a.display_name.toLowerCase().localeCompare(b.display_name.toLowerCase())
    );
  }, [data, args.includeSplitRules]);
};

export const useSelectAllRulesEntities = (args: GetRulesArgs) => {
  const rules = useSelectAllRules(args);

  return useMemo(() => toDictionary(rules), [rules]);
};

export const useSelectActiveRules = (args: GetRulesArgs) => {
  const allRules = useSelectAllRules(args);

  return useMemo(() => allRules.filter((r) => r.active), [allRules]);
};

export const useSelectActiveRulesEntities = (args: GetRulesArgs) => {
  const activeRules = useSelectActiveRules(args);

  return useMemo(() => toDictionary(activeRules), [activeRules]);
};

export const useSelectArchivedRules = (args: GetRulesArgs) => {
  const allRules = useSelectAllRules(args);

  return useMemo(() => allRules.filter((r) => !r.active), [allRules]);
};
